import {
  Button,
  DatePicker,
  Flex,
  Image,
  Input,
  Select,
  Space,
  TableProps,
  Tooltip,
} from "antd";
import dayjs, { Dayjs } from "dayjs";
import SearchIcon from "../../../../../assets/icons/SearchIcon";
import SelectDownIcon from "../../../../../assets/icons/SelectDownIcon";
import ViewIcon from "../../../../../assets/icons/ViewIcon";
import placeholder from "../../../../../assets/images/avatar.svg";
import StatusItem from "../../../../../components/common/StatusItem";
import ConfirmModal from "../../../../../components/ui/ConfirmModal";
import CustomTable from "../../../../../components/ui/CustomTable";
import { dbDateFormat } from "../../../../../constants/constants";
import {
  AllLeaveRequestParams,
  AllLeaveRequestResponse,
} from "../../../../../features/organization/contractual/LeaveManagement/leaveManagement.type";
import {
  useApproveOrRejectLeaveRequestMutation,
  useGetAllLeaveRequestQuery,
} from "../../../../../features/organization/contractual/LeaveManagement/leaveManagementApi";
import useModal from "../../../../../hooks/useModal";
import useTable from "../../../../../hooks/useTable";
import { formatDate } from "../../../../../lib/time";
import { toast } from "../../../../../lib/toast";
import { countParPageSerialNumber } from "../../../../../utils/number";
import { valueOrNa } from "../../../../../utils/string";
import { addKeyInArray, selectArrayOption } from "../../../../../utils/utils";
import CalanderIcon from "../../../survey/templates/_icons/CalanderIcon";
import ApproveActionIcon from "../_icons/ApproveActionIcon";
import RejectActionIcon from "../_icons/RejectActionIcon";
import RequestDetails from "./RequestDetails";

export const LEAVE_REQUEST_STATUES = [
  {
    label: "Pending",
    value: "pending",
    className: "status rounded-lg status-warning",
  },
  {
    label: "Approved",
    value: "approved",
    className: "status rounded-md status-success",
  },
  {
    label: "Rejected",
    value: "rejected",
    className: "status rounded-md status-danger",
  },
];

export default function LeaveRequests() {
  const { RangePicker } = DatePicker;
  const { open, handleOpen, handleClose } = useModal<
    any,
    "CONFIRM_APPROVE" | "CONFIRM_REJECT"
  >();
  const {
    page,
    limit,
    filter,
    keyword,
    handleLimit,
    handleFilter,
    handleSearch,
    handlePagination,
  } = useTable<AllLeaveRequestParams>();

  const { data, isLoading, isFetching } = useGetAllLeaveRequestQuery({
    params: { ...filter, search: keyword },
  });

  const [approveOrReject, { isLoading: updating }] =
    useApproveOrRejectLeaveRequestMutation();

  const handleApproveOrReject = async (type: "approved" | "rejected") => {
    try {
      await approveOrReject({
        id: open.data.id,
        data: { status: type },
      }).unwrap();
      toast("success", `Leave request ${type} successfully.`);
      handleClose();
    } catch (err) {
      console.error(err);
      toast("error", `Leave request ${type} failed`);
    }
  };

  const columns: TableProps<AllLeaveRequestResponse>["columns"] = [
    {
      title: "#SL",
      align: "center",
      width: 60,
      render: (_, __, index) => countParPageSerialNumber(page, limit, index),
    },

    {
      title: "Bondhu Name",
      dataIndex: "member",
      render: (value) => (
        <Flex align="center" gap={6}>
          <Image
            width={38}
            height={38}
            className="rounded-full"
            src={value?.image || placeholder}
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).src = placeholder;
            }}
          />
          <span>{valueOrNa(value.name)}</span>
        </Flex>
      ),
    },
    {
      title: "Bondhu ID",
      dataIndex: "business_member_id",
      render: (val) => valueOrNa(val),
    },
    {
      title: "Duration",
      dataIndex: "days_count",
      render: (val) => (
        <span>
          {valueOrNa(val)} {val > 1 ? "Days" : "Day"}
        </span>
      ),
    },
    {
      align: "center",
      title: "Applied Date",
      render: (record) =>
        `${formatDate(record?.from_date, "DD")}–${formatDate(record?.to_date)}`,
    },
    {
      title: "Leave Type",
      dataIndex: "leaveAllocation",
      render: (val) => valueOrNa(val?.leave_name),
    },
    {
      align: "center",
      title: "Status",
      dataIndex: "status",
      render: (val) => <StatusItem value={val} list={LEAVE_REQUEST_STATUES} />,
    },
    {
      title: "Action",
      align: "end",
      width: 130,
      render: (record) => {
        return (
          <Space align="center">
            {record.status === "pending" ? (
              <>
                <Tooltip title="Approve">
                  <Button
                    className="action-edit !outline-none mt-1"
                    size="small"
                    icon={<ApproveActionIcon />}
                    onClick={() =>
                      handleOpen({ type: "CONFIRM_APPROVE", data: record })
                    }
                  />
                </Tooltip>

                <Tooltip title="Reject">
                  <Button
                    className="action-delete !outline-none mt-1"
                    size="small"
                    icon={<RejectActionIcon />}
                    onClick={() =>
                      handleOpen({ type: "CONFIRM_REJECT", data: record })
                    }
                  />
                </Tooltip>
              </>
            ) : null}

            <Tooltip title="View">
              <Button
                className="action-view"
                size="small"
                icon={<ViewIcon />}
                onClick={() => handleOpen({ type: "VIEW", data: record })}
              />
            </Tooltip>
          </Space>
        );
      },
    },
  ];

  return (
    <>
      <CustomTable
        loading={isLoading || isFetching}
        limit={limit}
        page={page}
        columns={columns}
        total={data?.data.total || 0}
        dataSource={addKeyInArray(data?.data?.leaveRequests || [])}
        handleLimit={handleLimit}
        handlePagination={handlePagination}
        filterSection={
          <>
            <Input
              suffix={<SearchIcon />}
              placeholder="Search Name"
              style={{ minWidth: 170, maxWidth: 180 }}
              onChange={(val) => handleSearch(val)}
              allowClear
            />

            <RangePicker
              style={{ width: 245 }}
              separator="-"
              className="w-full input-bordered"
              format={"DD/MM/YYYY"}
              suffixIcon={<CalanderIcon />}
              placeholder={["dd/mm/yyyy", "dd/mm/yyyy"]}
              onChange={(dates: (Dayjs | null)[] | null) => {
                const [start, end] = dates || [];

                // Call filter only if both dates are selected
                if (start && end) {
                  handleFilter("from_date", dayjs(start).format(dbDateFormat));
                  handleFilter("to_date", dayjs(end).format(dbDateFormat));
                } else {
                  // Clear filters if user clears dates
                  handleFilter("from_date", undefined);
                  handleFilter("to_date", undefined);
                }
              }}
            />

            <Select
              allowClear
              placement="bottomRight"
              placeholder="Status"
              className="min-w-[90px]"
              rootClassName="icon-gray"
              popupMatchSelectWidth={120}
              suffixIcon={<SelectDownIcon />}
              onChange={(value) => handleFilter("status", value)}
              options={selectArrayOption(
                LEAVE_REQUEST_STATUES,
                "label",
                "value"
              )}
            />
          </>
        }
      />

      <RequestDetails
        data={open.data}
        handleClose={handleClose}
        open={open.open && open.type === "VIEW"}
      />

      <ConfirmModal
        width={450}
        open={open?.open && open.type === "CONFIRM_REJECT"}
        okLoading={updating}
        okText="Reject"
        title="Reject Leave Request?"
        handleClose={() => handleClose()}
        handleOk={() => handleApproveOrReject("rejected")}
        okButtonProps={{
          className: "!bg-red-500 !border-red-500 !text-white !min-w-[120px]",
          type: "default",
        }}
        subTitle="Are you sure you want to reject this leave request?"
      />

      <ConfirmModal
        width={450}
        open={open?.open && open.type === "CONFIRM_APPROVE"}
        okLoading={updating}
        okText="Approve"
        title="Approve Leave Request?"
        handleClose={() => handleClose()}
        handleOk={() => handleApproveOrReject("approved")}
        okButtonProps={{
          className:
            "!bg-green-600 !text-white !border-green-600 !min-w-[120px]",
          type: "default",
        }}
        subTitle="Are you sure you want to approve this leave request?"
      />
    </>
  );
}
